'use client';
import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import ModalPortal from './ModalPortal';
import Image from 'next/image';
import { XClose } from 'public/Images';

const Modal = ({
    children,
    open,
    setOpen,
    className,
    style,
    header,
}: {
    children: ReactElement;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    className?: string;
    header?: React.ReactNode;
    style?: CSSProperties;
}) => {
    const [visible, setVisible] = useState(open);
    const [animation, setAnimation] = useState(false);

    useEffect(() => {
        setVisible(open);
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.removeProperty('overflow');
        }

        const timeout = setTimeout(() => setAnimation(open), 5);

        return () => {
            clearTimeout(timeout);
            document.body.style.removeProperty('overflow');
        };
    }, [open]);

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [setOpen]);

    const handleOverlayClick = (event: React.MouseEvent) => {
        if (event.target === event.currentTarget) setOpen(false);
    };

    return (
        <ModalPortal>
            {visible && (
                <div
                    className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 transition-opacity duration-300 ${
                        animation ? 'opacity-100' : 'opacity-0'
                    }`}
                    onClick={handleOverlayClick}
                >
                    <div
                        className={`rounded-lg bg-white shadow-lg relative max-h-[80%] overflow-scroll transform transition-transform duration-300 ${
                            animation ? 'opacity-100' : 'opacity-0'
                        } ${className}`}
                        style={style}
                    >
                        {header && (
                            <HeaderWithXBtn header={header} setOpen={setOpen} />
                        )}
                        {children}
                    </div>
                </div>
            )}
        </ModalPortal>
    );
};

export default Modal;

const HeaderWithXBtn = ({
    header,
    setOpen,
}: {
    header: React.ReactNode;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    return (
        <div className="flex py-4 ps-4 pe-6 justify-between gap-3">
            {header ? header : <div />}
            <div
                onClick={() => setOpen(false)}
                className="flex items-center justify-center cursor-pointer w-5 h-5 rounded-full bg-gray-200"
            >
                <Image src={XClose} alt="x" />
            </div>
        </div>
    );
};
